'use client';

import type { FC } from 'react';
import { Button } from '@/components/button';
import MarkdownContent from '@/components/MarkdownContent';
import type { Bookmaker } from '@/types/bookmaker';
import { getBookmakerDetailsUrl } from '@/utils/bookmaker';
import { cn } from '@/utils/cn';
import { FlashIcon } from '../icons/FlashIcon';
import { LogoPiece3Icon } from '../icons/LogoPiece3Icon';
import { BookmakerLogoImage } from './components/BookmakerLogoImage';
import { CopyCode } from './components/CopyCode';
import { PaymentIcons } from './components/PaymentIcons';
import { RankingPercentage } from './components/RankingPercentage';
import { TopPickPadge } from './components/TopPickBadge';

interface TopPickCardProps {
  data: Bookmaker;
  type?: 'primary' | 'secondary';
}

export const TopPickCard: FC<TopPickCardProps> = ({
  data,
  type = 'primary',
}) => {
  const isSecondary = type === 'secondary';
  const {
    BookmakerUrl,
    Code,
    OurReviewList,
    PaymentType,
    BonusOffer,
    BookmakerLogo,
    Percentage,
    Slug,
    PayoutTime,
    BookmakerName,
  } = data.attributes;

  const detailArray = Object.values(OurReviewList);
  const bookmakerDetailsUrl = getBookmakerDetailsUrl(Slug);

  const containerClasses = cn(
    'w-full rounded-lg p-4',
    isSecondary
      ? 'bg-medium-gray'
      : 'bg-custom-gradient ring-1 ring-light-blue-o25'
  );

  return (
    <>
      {/* Mobile Layout */}
      <div className={cn(containerClasses, 'flex flex-col gap-2 lg:hidden')}>
        <div className="flex flex-col gap-3">
          <LogoSection
            BookmakerLogo={BookmakerLogo}
            BookmakerName={BookmakerName}
            BookmakerUrl={BookmakerUrl}
          />
          <div className="grid grid-cols-2 gap-3">
            <RankingPercentage
              className="w-full"
              hasRanking={!isSecondary}
              percentage={Percentage}
            />
            {!isSecondary && <TopPickPadge />}
          </div>
        </div>

        <div className="grid flex-1 grid-cols-2 gap-3">
          <BonusOfferSection
            BonusOffer={BonusOffer}
            BookmakerName={BookmakerName}
          />
          <CopyCode className="flex-1" code={Code} />
        </div>

        <div className="grid grid-cols-2 justify-between gap-3">
          <PaymentSection PaymentType={PaymentType} />
          <PayoutSection PayoutTime={PayoutTime} />
        </div>

        <div className="flex flex-col gap-8">
          <div className="flex max-w-full flex-1 items-center">
            <ReviewSection detailArray={detailArray} />
          </div>
          <ActionButtons
            BookmakerUrl={BookmakerUrl}
            bookmakerDetailsUrl={bookmakerDetailsUrl}
          />
        </div>
      </div>

      {/* Desktop Layout */}
      <div
        className={cn(
          containerClasses,
          'hidden grid-cols-[176px_176px_152px_152px_auto] gap-4 lg:grid'
        )}
      >
        <div className="flex flex-col gap-2">
          {!isSecondary && <TopPickPadge />}
          <LogoSection
            BookmakerLogo={BookmakerLogo}
            BookmakerName={BookmakerName}
            BookmakerUrl={BookmakerUrl}
          />
          <RankingPercentage
            className="w-44"
            hasRanking={!isSecondary}
            percentage={Percentage}
          />
        </div>

        <div className="flex min-w-36">
          <BonusOfferSection
            BonusOffer={BonusOffer}
            BookmakerName={BookmakerName}
          />
        </div>

        <div className="flex flex-col gap-4">
          <PaymentSection PaymentType={PaymentType} />
          <PayoutSection PayoutTime={PayoutTime} />
        </div>

        <CopyCode className="min-w-36" code={Code} />

        <div className="flex flex-row gap-8">
          <div className="flex max-w-full flex-1 items-center">
            <ReviewSection detailArray={detailArray} />
          </div>
          <ActionButtons
            BookmakerUrl={BookmakerUrl}
            bookmakerDetailsUrl={bookmakerDetailsUrl}
          />
        </div>
      </div>
    </>
  );
};

function LogoSection({
  BookmakerLogo,
  BookmakerName,
  BookmakerUrl,
}: {
  BookmakerLogo: Bookmaker['attributes']['BookmakerLogo'];
  BookmakerName: Bookmaker['attributes']['BookmakerName'];
  BookmakerUrl: Bookmaker['attributes']['BookmakerUrl'];
}) {
  const imageUrl = BookmakerLogo.data.attributes.url;

  return imageUrl ? (
    <BookmakerLogoImage
      alt={`${BookmakerName} logo`}
      imageUrl={imageUrl}
      url={BookmakerUrl}
    />
  ) : null;
}

function BonusOfferSection({
  BookmakerName,
  BonusOffer,
}: {
  BookmakerName: Bookmaker['attributes']['BookmakerName'];
  BonusOffer: Bookmaker['attributes']['BonusOffer'];
}) {
  return (
    <div className="relative flex-1 overflow-hidden rounded-lg border border-light-blue-o15 bg-light-blue-o15 p-3">
      <div className="absolute right-0 bottom-0">
        <LogoPiece3Icon
          className="h-auto w-20"
          desc="GoCore logo piece decoration"
          title="Logo Decoration"
        />
      </div>
      <p className="mb-1 text-gray text-xs">{BookmakerName} offer</p>
      <div className="relative z-10">
        <MarkdownContent>{BonusOffer}</MarkdownContent>
      </div>
    </div>
  );
}

function PaymentSection({
  PaymentType,
}: {
  PaymentType: Bookmaker['attributes']['PaymentType'];
}) {
  return (
    <div className="flex-1 rounded-lg bg-dark-gray p-3">
      <p className="mb-2 text-gray text-xs">Accepted payments</p>
      <PaymentIcons paymentType={PaymentType} />
    </div>
  );
}

function PayoutSection({
  PayoutTime,
}: {
  PayoutTime: Bookmaker['attributes']['PayoutTime'];
}) {
  return (
    <div className="flex-1 rounded-lg bg-dark-gray p-3">
      <p className="mb-2 text-gray text-xs">Payout time</p>
      <div className="flex items-center gap-1">
        <FlashIcon desc="Fast payout time icon" title="Fast Payout" />
        <p className="font-medium text-sm">{PayoutTime}</p>
      </div>
    </div>
  );
}

function ReviewSection({ detailArray }: { detailArray: string[] }) {
  return (
    <div className="py-4 lg:py-0">
      <p className="mb-2 font-semibold text-gray text-sm">Our review</p>
      <ul className="ml-5 list-disc space-y-1">
        {detailArray.slice(0, 5).map((item: string) => (
          <li className="text-[#F8F8F8] text-xs" key={item}>
            {item}
          </li>
        ))}
      </ul>
    </div>
  );
}

function ActionButtons({
  BookmakerUrl,
  bookmakerDetailsUrl,
}: {
  BookmakerUrl: Bookmaker['attributes']['BookmakerUrl'];
  bookmakerDetailsUrl: string;
}) {
  return (
    <div className="flex flex-col justify-center gap-2">
      <div className="flex flex-row-reverse gap-2 lg:flex-col">
        <div className="flex-1">
          <Button
            as="link"
            href={BookmakerUrl}
            target="_blank"
            variant="primary"
          >
            Visit Site
          </Button>
        </div>
        <div className="flex-1">
          <Button as="link" href={bookmakerDetailsUrl} variant="ghost">
            View full review
          </Button>
        </div>
      </div>
    </div>
  );
}
